import { Tabs, type TabsProps } from '@mui/material'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'

/**
 * Slack only when leaving scrollable → fullWidth (avoids border flap).
 * Enter scrollable when natural row width overflows OR a fullWidth tab clips its label.
 */
const FIT_HYSTERESIS_PX = 24

/** True when fullWidth has squeezed a label (scrollWidth > visible box). */
function anyTabLabelClipped(root: HTMLElement): boolean {
  const tabs = Array.from(root.querySelectorAll<HTMLElement>('.MuiTab-root'))
  for (const tab of tabs) {
    if (tab.scrollWidth > tab.clientWidth + 1) return true
  }
  return false
}

function measureNaturalWidth(root: HTMLElement): number | null {
  const flex = root.querySelector('.MuiTabs-flexContainer') as HTMLElement | null
  if (!flex) return null

  const tabs = Array.from(flex.querySelectorAll<HTMLElement>('.MuiTab-root'))
  if (tabs.length === 0) return null

  const backups = tabs.map(el => ({
    flex: el.style.flex,
    flexGrow: el.style.flexGrow,
    flexShrink: el.style.flexShrink,
    flexBasis: el.style.flexBasis,
    width: el.style.width,
    maxWidth: el.style.maxWidth,
    minWidth: el.style.minWidth,
  }))

  // max-content: real label width (incl. padding), not under-measured 90px mins.
  tabs.forEach(el => {
    el.style.flex = '0 0 auto'
    el.style.flexGrow = '0'
    el.style.flexShrink = '0'
    el.style.flexBasis = 'auto'
    el.style.width = 'max-content'
    el.style.maxWidth = 'none'
    el.style.minWidth = 'max-content'
  })

  const needed = flex.scrollWidth

  backups.forEach((b, i) => {
    const el = tabs[i]
    el.style.flex = b.flex
    el.style.flexGrow = b.flexGrow
    el.style.flexShrink = b.flexShrink
    el.style.flexBasis = b.flexBasis
    el.style.width = b.width
    el.style.maxWidth = b.maxWidth
    el.style.minWidth = b.minWidth
  })

  return needed
}

function decideFits(needed: number, rootWidth: number, currentlyFits: boolean): boolean {
  if (currentlyFits) {
    return needed <= rootWidth + 1
  }
  return needed <= rootWidth - FIT_HYSTERESIS_PX
}

export type AdaptiveTabsProps = Omit<TabsProps, 'variant' | 'scrollButtons'>

/**
 * fullWidth when labels fit; otherwise scrollable with arrows, wheel and pointer-drag.
 * MUI scroller uses overflow-x:hidden — without these, overflow tabs are unreachable.
 */
const AdaptiveTabs = ({
  children,
  allowScrollButtonsMobile = true,
  sx,
  ...props
}: AdaptiveTabsProps) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const [fits, setFits] = useState(true)
  const fitsRef = useRef(fits)
  fitsRef.current = fits

  const dragRef = useRef<{
    pointerId: number
    startX: number
    startScroll: number
    moved: boolean
  } | null>(null)
  const suppressClickRef = useRef(false)

  useLayoutEffect(() => {
    const root = rootRef.current
    if (!root) return

    let frame = 0
    const update = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        if (!root.isConnected || root.clientWidth < 8) return
        if (fitsRef.current && anyTabLabelClipped(root)) {
          setFits(false)
          return
        }
        const needed = measureNaturalWidth(root)
        if (needed == null) return
        const next = decideFits(needed, root.clientWidth, fitsRef.current)
        setFits(prev => (prev === next ? prev : next))
      })
    }

    update()

    const ro = new ResizeObserver(update)
    ro.observe(root)

    const flex = root.querySelector('.MuiTabs-flexContainer')
    const mo = new MutationObserver(update)
    if (flex) {
      mo.observe(flex, { childList: true, subtree: true, characterData: true })
    }

    return () => {
      cancelAnimationFrame(frame)
      ro.disconnect()
      mo.disconnect()
    }
  }, [children])

  useEffect(() => {
    if (fits) return
    const root = rootRef.current
    if (!root) return

    const getScroller = () => root.querySelector('.MuiTabs-scroller') as HTMLElement | null

    const onWheel = (event: WheelEvent) => {
      const el = getScroller()
      if (!el) return
      const delta =
        Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY
      if (delta === 0) return
      const maxScroll = el.scrollWidth - el.clientWidth
      if (maxScroll <= 0) return
      const next = Math.min(maxScroll, Math.max(0, el.scrollLeft + delta))
      if (next === el.scrollLeft) return
      el.scrollLeft = next
      event.preventDefault()
      event.stopPropagation()
    }

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return
      if ((event.target as HTMLElement | null)?.closest?.('.MuiTabScrollButton-root')) return
      const el = getScroller()
      if (!el || el.scrollWidth <= el.clientWidth) return
      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScroll: el.scrollLeft,
        moved: false,
      }
      try {
        root.setPointerCapture(event.pointerId)
      } catch {
        /* ignore */
      }
    }

    const onPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== event.pointerId) return
      const el = getScroller()
      if (!el) return
      const dx = event.clientX - drag.startX
      if (!drag.moved && Math.abs(dx) < 4) return
      drag.moved = true
      el.scrollLeft = drag.startScroll - dx
      event.preventDefault()
    }

    const onPointerUp = (event: PointerEvent) => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== event.pointerId) return
      suppressClickRef.current = drag.moved
      dragRef.current = null
      try {
        root.releasePointerCapture(event.pointerId)
      } catch {
        /* ignore */
      }
    }

    const onClickCapture = (event: MouseEvent) => {
      if (!suppressClickRef.current) return
      suppressClickRef.current = false
      event.preventDefault()
      event.stopPropagation()
    }

    root.addEventListener('wheel', onWheel, { passive: false })
    root.addEventListener('pointerdown', onPointerDown)
    root.addEventListener('pointermove', onPointerMove)
    root.addEventListener('pointerup', onPointerUp)
    root.addEventListener('pointercancel', onPointerUp)
    root.addEventListener('click', onClickCapture, true)

    return () => {
      root.removeEventListener('wheel', onWheel)
      root.removeEventListener('pointerdown', onPointerDown)
      root.removeEventListener('pointermove', onPointerMove)
      root.removeEventListener('pointerup', onPointerUp)
      root.removeEventListener('pointercancel', onPointerUp)
      root.removeEventListener('click', onClickCapture, true)
    }
  }, [fits])

  return (
    <Tabs
      {...props}
      ref={rootRef}
      variant={fits ? 'fullWidth' : 'scrollable'}
      scrollButtons={!fits}
      allowScrollButtonsMobile={allowScrollButtonsMobile}
      sx={{
        minWidth: 0,
        width: '100%',
        // Default bar height when call site omits height (e.g. Dashboard).
        // Do NOT use height:100% on tabs/buttons — % of auto height breaks scroll-button
        // alignment (arrows sit above the tab row).
        minHeight: 48,
        overflow: 'hidden',
        overscrollBehaviorX: 'contain',
        '& .MuiTabs-scroller': {
          overflow: 'hidden',
          overscrollBehaviorX: 'contain',
        },
        '& .MuiTabs-flexContainer': {
          alignItems: 'stretch',
        },
        '& .MuiTab-root': {
          minHeight: 'inherit',
          boxSizing: 'border-box',
        },
        '& .MuiTabScrollButton-root': {
          flexShrink: 0,
          alignSelf: 'stretch',
        },
        ...sx,
        ...(!fits && {
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          '& .MuiTab-root': {
            minHeight: 'inherit',
            boxSizing: 'border-box',
            flexShrink: 0,
            minWidth: 'max-content',
            touchAction: 'none',
          },
        }),
      }}
    >
      {children}
    </Tabs>
  )
}

export default AdaptiveTabs
